/**
 * FL Posteingang — Top-Level Menüpunkt „Steuerberater" für den Freelancer.
 *
 * Layout (folgt FL-App-Design-Sprache):
 *   - Header: Greeting + Magic-Link-Share
 *   - Card 1: Mein Steuerberater (Kontakt)
 *   - Card 2: Was sieht mein SB? (Permission-Grid)
 *   - Period-Status (Q1-Q4 Chips)
 *   - CollabInbox (Two-Pane mit Threading)
 *   - Collapsible: Klassischer ZIP-Export für FA
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Briefcase, UserPlus, ArrowRight, FileDown, Mail, Building2,
  CheckCircle2, Circle, Clock, Lock, ChevronDown, ChevronRight, Copy,
  ShieldCheck, Calendar,
} from 'lucide-react';
import CollabInbox from '@/components/CollabInbox';
import Tresor from '@/components/Tresor';
import ClosuresGrid from '@/components/ClosuresGrid';
import UpgradeRequiredView from '@/components/UpgradeRequiredView';
import { useIsPro } from '@/hooks/useIsPro';
import { Briefcase as BriefcaseIcon } from 'lucide-react';
import { AdvisorChangeLog, AdvisorAccessSection } from '@/pages/settings/AdvisorAccessSection';

const H = { fontFamily: 'Manrope, sans-serif' };

const PERIOD_STATUS_META = {
  open: { label: 'Offen', cls: 'bg-slate-50 text-slate-600 border-slate-200', Icon: Circle },
  in_review: { label: 'In Prüfung', cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: Clock },
  feedback: { label: 'Rückfragen', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Clock },
  approved: { label: 'Freigegeben', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  locked: { label: 'Festgeschrieben', cls: 'bg-slate-100 text-slate-600 border-slate-300', Icon: Lock },
};

// PERMISSION_LABELS removed (P1b-Simplification, Feb 2026): Read-Access ist
// jetzt immer True für aktive Verbindungen, Granular-Grid wurde entfernt.

// C5-Cleanup (Feb 2026): MagicLinkDialog entfernt — Magic-Link-Funktion nicht mehr Teil des UI.

export default function SteuerberaterTab() {
  const { user } = useAuth();
  const isPro = useIsPro();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [periodReviews, setPeriodReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inbox');

  // Pro-Gating (Iter 73i): Free-User sehen In-Page-Upgrade-Card statt SB-Workflow.
  if (!isPro) {
    return (
      <UpgradeRequiredView
        source="steuerberater"
        icon={BriefcaseIcon}
        title="Steuerberater-Anbindung"
        subtitle="Direkter Draht zu deinem Steuerberater — DSGVO-konform und ohne E-Mail-Pingpong."
        features={[
          'Posteingang mit Threads, Anhängen und Statusverlauf',
          'Dokumente: GoBD-konformer Dokumenten-Speicher mit Versionierung',
          'Quartalsweise Periodenabschlüsse mit Pre-Check und PDF-Bericht',
          'Direkter ZIP-Export für Finanzamt-Abgaben',
          'AVV / Vollmacht digital — keine Papierkram-Schleifen mehr',
        ]}
      />
    );
  }

  const handleExport = useCallback(async () => {
    if (!user?.id) return;
    try {
      toast.info('Export wird vorbereitet…');
      const res = await api.get(`/users/${user.id}/export-zip`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ivoliq_export_${user.name?.replace(/\s+/g, '_') || 'data'}_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export heruntergeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export fehlgeschlagen');
    }
  }, [user]);

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [summaryRes, periodsRes] = await Promise.all([
        api.get(`/advisor/collaboration-summary/${user.id}`),
        api.get(`/advisor/clients/${user.id}/period-reviews`).catch(() => ({ data: { reviews: [] } })),
      ]);
      setSummary(summaryRes.data);
      setPeriodReviews(periodsRes.data.reviews || []);
      api.post(`/advisor/mark-seen/${user.id}`).catch((e) => {
        console.warn('[SteuerberaterTab] mark-seen failed:', e?.response?.status, e?.message);
      });
    } catch (err) {
      // Erwartete Zustände: kein SB verbunden (404/403). Höhere Fehler loggen wir.
      if (err?.response?.status >= 500) {
        console.warn('[SteuerberaterTab] fetchData failed:', err?.response?.status, err?.message);
      }
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Period reviews: dedup by (year, quarter) and filter out invalid entries.
  // MUST be declared before any early-return to keep hook order stable.
  const reviewsSorted = React.useMemo(() => {
    const seen = new Set();
    return [...periodReviews]
      .filter((r) => r && Number.isFinite(r.year) && Number.isFinite(r.quarter))
      .filter((r) => {
        const key = `${r.year}-${r.quarter}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => {
        if (b.year !== a.year) return b.year - a.year;
        return (a.quarter || 0) - (b.quarter || 0);
      });
  }, [periodReviews]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="fl-inbox-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-emerald-600" />
      </div>
    );
  }

  // No advisor connected → CTA + Inline-Einlade-UI (Feb 2026: Issue #2 — alles im SB-Tab)
  if (!summary?.has_advisor) {
    return (
      <div className="max-w-3xl mx-auto py-10" data-testid="fl-inbox-no-advisor">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Briefcase className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-2 tracking-tight" style={H}>
            Noch kein Steuerberater verbunden
          </h2>
          <p className="text-sm text-slate-500 mb-2 leading-relaxed max-w-md mx-auto">
            Lade deinen Steuerberater unten direkt ein — oder lade vorab Daten herunter.
          </p>
          <div className="mt-4">
            <Button variant="outline" onClick={handleExport} data-testid="export-no-advisor-btn" size="sm">
              <FileDown className="w-4 h-4 mr-2" />
              Datenexport (ZIP)
            </Button>
          </div>
        </div>
        {/* Inline AdvisorAccessSection — Einladung direkt hier statt Redirect zu /settings */}
        <AdvisorAccessSection />
      </div>
    );
  }

  const advisor = summary.advisors?.[0];
  const permissions = advisor?.permissions || {};

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 11) return 'Guten Morgen';
    if (h < 18) return 'Guten Tag';
    return 'Guten Abend';
  })();
  const firstName = (user?.name || '').split(' ')[0];

  return (
    <div className="space-y-6" data-testid="fl-inbox">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={H} data-testid="fl-inbox-greeting">
            Steuerberater
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {greeting}{firstName ? `, ${firstName}` : ''} — alles zwischen dir und {advisor?.name || advisor?.email || 'deinem Steuerberater'}.
          </p>
          {user?.created_by_advisor_name && (
            <p className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-[11px] text-emerald-800" data-testid="onboarded-by-advisor-badge">
              Account eingerichtet durch {user.created_by_advisor_name}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={handleExport} data-testid="export-btn">
            <FileDown className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Two info cards */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Advisor */}
        <Card>
          <CardContent className="p-5">
            <p className="mb-3 text-xs font-medium uppercase tracking-[0.05em] text-slate-500">Mein Steuerberater</p>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 font-mono text-xs font-semibold text-emerald-700">
                {(advisor?.name || advisor?.email || '?').slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 truncate" data-testid="advisor-name-fl">{advisor?.name || 'Unbekannt'}</p>
                <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {advisor?.company_name || advisor?.email}
                </p>
                {advisor?.email && (
                  <p className="text-xs text-slate-500 truncate flex items-center gap-1 mt-0.5">
                    <Mail className="h-3 w-3" />
                    {advisor.email}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* P1b-Simplification (Feb 2026):
         * Granulares „Was sieht dein Steuerberater?"-Grid entfernt — Read-Access
         * ist jetzt immer aktiv. Card zeigt nur noch den Vollmacht-Status. */}
        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <div className="mb-3 flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">Zugriffsrechte</p>
            </div>

            {/* Read-Access (immer aktiv) */}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-900 mb-2.5" data-testid="read-access-info">
              <div className="flex items-center gap-2 font-medium">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                Lesezugriff aktiv
              </div>
              <p className="mt-1 leading-relaxed text-emerald-900/80">
                Dein Steuerberater sieht Dashboard, Rechnungen, Ausgaben, Belege und Steuern — Standard für jede aktive Verbindung.
              </p>
            </div>

            {/* Vollmacht (Edit Permission) — explicit text + logging note */}
            <div className={`rounded-lg border px-3 py-2.5 text-xs ${permissions.edit ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-slate-50'}`} data-testid="vollmacht-card">
              <div className="flex items-center gap-2 font-medium">
                <ShieldCheck className={`h-3.5 w-3.5 ${permissions.edit ? 'text-emerald-600' : 'text-slate-400'}`} />
                <span className={permissions.edit ? 'text-emerald-900' : 'text-slate-700'}>
                  Vollmacht: {permissions.edit ? 'aktiviert' : 'deaktiviert'}
                </span>
              </div>
              <p className={`mt-1 leading-relaxed ${permissions.edit ? 'text-emerald-900/80' : 'text-slate-500'}`}>
                Mit aktivierter Vollmacht kann dein Steuerberater Belege & Buchungen direkt korrigieren. Jede Änderung wird DSGVO-konform geloggt und ist im Änderungslog einsehbar.
              </p>
            </div>
            <p className="mt-3 text-[11px] text-slate-400">
              Anpassen unter <button onClick={() => navigate('/settings?tab=advisor')} className="underline hover:text-emerald-700">Einstellungen → Steuerberater-Zugang</button>.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Period Status (compact) */}
      {reviewsSorted.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="mb-2 flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-slate-400" />
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Quartalsstatus</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {reviewsSorted.slice(0, 8).map((r) => {
                const meta = PERIOD_STATUS_META[r.status] || PERIOD_STATUS_META.open;
                return (
                  <div key={`${r.year}-${r.quarter}`} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5">
                    <span className="font-mono text-xs font-semibold text-slate-700">Q{r.quarter} {r.year}</span>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${meta.cls}`}>
                      <meta.Icon className="h-2.5 w-2.5" />
                      {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inbox / Tresor / Abschlüsse / Änderungslog Tabs */}
      <div className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 flex-wrap">
        {[
          { key: 'inbox', label: 'Posteingang' },
          { key: 'tresor', label: 'Dokumente' },
          { key: 'closures', label: 'Abschlüsse' },
          { key: 'log', label: 'Änderungslog' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`rounded-md px-4 py-1.5 text-sm transition-colors ${
              activeTab === t.key ? 'bg-emerald-50 font-medium text-emerald-700' : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'inbox' ? (
        <CollabInbox mode="freelancer" clientId={user?.id} initialFilter="mine" />
      ) : activeTab === 'tresor' ? (
        <Tresor mode="freelancer" clientId={user?.id} />
      ) : activeTab === 'closures' ? (
        <ClosuresGrid />
      ) : (
        <AdvisorChangeLog />
      )}
    </div>
  );
}
